import { useState, useRef, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import API from '../lib/api';
import { placeStockOrder, getAlpacaAccountId } from '../lib/alpaca';

const formatUSD = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Steps: amount → confirm → verify → executing → done
export default function RoboAdvisor({ portfolio, onClose }) {
  const snap = useSnapshot(state);
  const accountId = getAlpacaAccountId(snap.user?.email);
  const phone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : '';

  const [step, setStep] = useState('amount'); // amount | confirm | verify | executing | done | error
  const [amount, setAmount] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [codeError, setCodeError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [orders, setOrders] = useState([]);
  const [orderResults, setOrderResults] = useState([]);
  const [orderErrors, setOrderErrors] = useState([]);
  const [sendingCode, setSendingCode] = useState(false);
  const codeRefs = useRef([]);

  // Los portafolios multimoneda traen `posiciones` (COP + USD); los viejos,
  // `composition` (solo USD). Se aceptan los dos para no romper nada.
  const allocation = portfolio.posiciones || portfolio.composition || portfolio.allocation || [];
  const totalAmount = parseFloat(amount) || 0;

  const conMonto = allocation.map((item) => ({
    ...item,
    symbol: item.symbol || item.ticker || item.fondo,
    // Un fondo de Progresion NO se puede comprar hasta que se firme. El default
    // es ejecutable para que los portafolios viejos (sin el campo) sigan igual.
    ejecutable: item.ejecutable !== false,
    amount: Math.round((item.pct / 100) * totalAmount * 100) / 100,
  }));

  // LA SEPARACION QUE IMPIDE EL ENGANO. Si el cliente pone $1.000 en un
  // portafolio 40/60, HOY solo se invierten $600: los $400 en pesos no se
  // pueden comprar todavia. Ese dinero NO se debita ni se aparta — se queda en
  // su saldo. Cobrarle por algo que no se compro seria lo peor que podriamos
  // hacer, y mostrarle "invertimos $1.000" seria mentirle.
  const breakdown = conMonto.filter((x) => x.ejecutable);
  const enVitrina = conMonto.filter((x) => !x.ejecutable);
  const montoHoy = Math.round(breakdown.reduce((a, x) => a + x.amount, 0) * 100) / 100;
  const montoEspera = Math.round(enVitrina.reduce((a, x) => a + x.amount, 0) * 100) / 100;

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleAmountNext = () => {
    if (totalAmount < 1) return;
    setStep('confirm');
  };

  const handleConfirmNext = async () => {
    setSendingCode(true);
    try {
      await API.post('auth/sendCodeToPhone', { phonenumber: phone });
      setResendTimer(60);
      setStep('verify');
    } catch (e) {
      console.error('send code:', e);
      setCodeError('Error enviando código. Intenta de nuevo.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    setSendingCode(true);
    try {
      await API.post('auth/sendCodeToPhone', { phonenumber: phone });
      setResendTimer(60);
      setCodeError('');
    } catch (e) {
      setCodeError('Error reenviando código.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setCodeError('');
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyAndExecute = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setCodeError('Ingresa el código de 6 dígitos');
      return;
    }

    // Verify code
    try {
      const res = await API.post('auth/verifyCode', { phonenumber: phone, code: fullCode });
      if (!res.data || res.data.success === 'error') {
        setCodeError('Código incorrecto. Intenta de nuevo.');
        setCode(['', '', '', '', '', '']);
        codeRefs.current[0]?.focus();
        return;
      }
    } catch (e) {
      setCodeError('Código incorrecto o expirado.');
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
      return;
    }

    // Code verified — execute orders
    setStep('executing');
    const results = [];
    const errors = [];

    for (const item of breakdown) {
      if (item.amount < 1) {
        errors.push({ symbol: item.symbol, error: 'Monto mínimo $1 USD' });
        continue;
      }
      try {
        const result = await placeStockOrder({
          symbol: item.symbol,
          notional: item.amount,
          side: 'buy',
          accountId,
        });
        results.push({ symbol: item.symbol, ...result });
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Error desconocido';
        errors.push({ symbol: item.symbol, error: msg });
      }
    }

    setOrderResults(results);
    setOrderErrors(errors);
    if (results.length > 0) {
      localStorage.setItem('fp_robo_used', 'true');
    }
    setStep('done');
  };

  const quickAmounts = [25, 50, 100, 200, 500];

  // STEP: Enter amount
  if (step === 'amount') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#2A59C6]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="text-lg font-bold text-[#181D27]">Invertir en mi plan</h2>
          <div className="w-5" />
        </div>

        <div className="bg-[#F0F4FF] rounded-xl p-3 mb-6 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm" style={{ backgroundColor: portfolio.color }}>
            {portfolio.icon === 'shield' ? '\u{1F6E1}' : portfolio.icon === 'balance' ? '⚖' : '\u{1F680}'}
          </div>
          <div>
            <div className="text-sm font-semibold text-[#181D27]">Portafolio {portfolio.name}</div>
            <div className="text-xs text-[#6B7280]">{portfolio.risk}</div>
          </div>
        </div>

        <div className="text-center mb-6">
          <label className="text-sm text-[#6B7280] block mb-2">¿Cuánto quieres invertir?</label>
          <div className="relative inline-block">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-bold text-[#181D27]">$</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="1"
              step="0.01"
              className="text-3xl font-bold text-center text-[#181D27] w-64 py-4 pl-10 pr-4 border-2 border-[#E5E7EB] rounded-2xl focus:border-[#2A59C6] outline-none"
              autoFocus
            />
          </div>
          <div className="text-xs text-[#9CA3AF] mt-2">Mínimo $1 USD</div>
        </div>

        <div className="flex gap-2 justify-center mb-8 flex-wrap">
          {quickAmounts.map((q) => (
            <button
              key={q}
              onClick={() => setAmount(String(q))}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                amount === String(q) ? 'bg-[#2A59C6] text-white border-[#2A59C6]' : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#2A59C6]'
              }`}
            >
              ${q}
            </button>
          ))}
        </div>

        {totalAmount >= 1 && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-6">
            <div className="text-xs font-semibold text-[#181D27] mb-3">
              {enVitrina.length ? 'Se compra HOY (dólares):' : 'El robo-advisor comprará:'}
            </div>
            <div className="space-y-2">
              {breakdown.map((item) => (
                <div key={item.symbol} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color || '#2A59C6' }} />
                    <span className="text-sm text-[#181D27]">{item.symbol}</span>
                    <span className="text-xs text-[#9CA3AF]">{item.pct}%</span>
                  </div>
                  <span className="text-sm font-semibold text-[#181D27]">{formatUSD(item.amount)}</span>
                </div>
              ))}
            </div>

            {/* LA PARTE EN VITRINA. Va DEBAJO y en gris a proposito: se ve que
                es parte de su portafolio, y se ve que hoy no se compra. Ocultarla
                seria mentir por omision; mostrarla igual que lo demas, tambien. */}
            {enVitrina.length > 0 && (
              <div className="mt-4 pt-4 border-t border-dashed border-[#E5E7EB]">
                <div className="text-xs font-semibold text-[#9CA3AF] mb-2">
                  En pesos — todavía no se puede comprar:
                </div>
                <div className="space-y-2 opacity-60">
                  {enVitrina.map((item) => (
                    <div key={item.symbol} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#9CA3AF]" />
                        <span className="text-sm text-[#6B7280]">{item.symbol}</span>
                        <span className="text-xs text-[#9CA3AF]">{item.pct}%</span>
                      </div>
                      <span className="text-sm text-[#6B7280]">{formatUSD(item.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-[11px] leading-snug text-[#B54708] bg-[#FFFAEB] border border-[#F4D68C] rounded-lg px-3 py-2">
                  Estos son fondos colombianos de Progresión. Estamos terminando la
                  conexión y muy pronto los vas a poder comprar desde aquí —{' '}
                  <strong>hoy no se compran y no se te cobra nada por ellos</strong>.
                  Esa plata se queda en tu saldo.
                </div>
              </div>
            )}

            {/* El numero que de verdad importa: cuanto sale de su bolsillo HOY. */}
            {enVitrina.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
                <span className="text-sm font-bold text-[#181D27]">Se invierte hoy</span>
                <span className="text-base font-extrabold text-[#181D27]">{formatUSD(montoHoy)}</span>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleAmountNext}
          disabled={totalAmount < 1}
          className={`w-full py-3.5 rounded-xl text-sm font-semibold transition ${
            totalAmount >= 1 ? 'bg-[#2A59C6] text-white hover:bg-[#1E4BB8]' : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
          }`}
        >
          Continuar
        </button>
      </div>
    );
  }

  // STEP: Confirm order details
  if (step === 'confirm') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setStep('amount')} className="text-[#6B7280] hover:text-[#2A59C6]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="text-lg font-bold text-[#181D27]">Confirmar inversión</h2>
          <div className="w-5" />
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="text-center mb-5">
            <div className="text-xs text-[#6B7280] mb-1">{enVitrina.length ? 'Se invierte hoy' : 'Total a invertir'}</div>
            <div className="text-3xl font-bold text-[#181D27]">{formatUSD(enVitrina.length ? montoHoy : totalAmount)}</div>
            <div className="text-xs text-[#9CA3AF] mt-1">Portafolio {portfolio.name} · {portfolio.risk}</div>
            {enVitrina.length > 0 && (
              <div className="text-[11px] text-[#B54708] mt-2">
                {formatUSD(montoEspera)} de tu portafolio son fondos colombianos: no se compran hoy
                y se quedan en tu saldo.
              </div>
            )}
          </div>

          <div className="border-t border-[#F3F4F6] pt-4">
            <div className="text-xs font-semibold text-[#181D27] mb-3">Detalle de compras</div>
            <div className="space-y-3">
              {breakdown.map((item) => (
                <div key={item.symbol} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-xs font-bold" style={{ color: item.color }}>
                      {item.symbol.slice(0, 2)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[#181D27]">{item.symbol}</div>
                      <div className="text-xs text-[#6B7280]">{item.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#181D27]">{formatUSD(item.amount)}</div>
                    <div className="text-xs text-[#9CA3AF]">{item.pct}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#FFFBEB] rounded-xl p-3 mb-4 flex gap-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" className="flex-shrink-0 mt-0.5"><path d="M12 9v2m0 4h.01M10.29 3.86l-8.56 14.77A2 2 0 003.45 21h17.1a2 2 0 001.72-2.96L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
          <div className="text-xs text-[#92400E]">
            <strong>Orden de mercado.</strong> Los precios pueden variar ligeramente al momento de la ejecución. Las órdenes se ejecutan al mejor precio disponible.
          </div>
        </div>

        <div className="bg-[#F0F4FF] rounded-xl p-3 mb-6 flex gap-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A59C6" strokeWidth="2" className="flex-shrink-0 mt-0.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <div className="text-xs text-[#1E40AF]">
            <strong>Verificación requerida.</strong> Te enviaremos un código SMS al {phone ? `****${phone.slice(-4)}` : 'teléfono registrado'} para confirmar esta operación.
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setStep('amount')}
            className="flex-1 py-3.5 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB] transition"
          >
            Modificar
          </button>
          <button
            onClick={handleConfirmNext}
            disabled={sendingCode}
            className="flex-1 py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition disabled:bg-[#93B4F5]"
          >
            {sendingCode ? 'Enviando código...' : 'Confirmar y verificar'}
          </button>
        </div>
      </div>
    );
  }

  // STEP: SMS verification
  if (step === 'verify') {
    return (
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setStep('confirm')} className="text-[#6B7280] hover:text-[#2A59C6]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="text-lg font-bold text-[#181D27]">Verificar operación</h2>
          <div className="w-5" />
        </div>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-[#F0F4FF] mx-auto mb-4 flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2A59C6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
          </div>
          <h3 className="text-lg font-bold text-[#181D27] mb-1">Código de verificación</h3>
          <p className="text-sm text-[#6B7280]">
            Enviamos un código SMS al {phone ? `****${phone.slice(-4)}` : 'teléfono registrado'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
          <div className="text-xs text-[#6B7280] mb-2 text-center">Estás aprobando:</div>
          <div className="text-center">
            <span className="text-lg font-bold text-[#181D27]">{formatUSD(enVitrina.length ? montoHoy : totalAmount)}</span>
            <span className="text-sm text-[#6B7280] ml-2">en Portafolio {portfolio.name}</span>
          </div>
        </div>

        <div className="flex gap-2 justify-center mb-4">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (codeRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(i, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(i, e)}
              className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 outline-none transition ${
                codeError ? 'border-red-400' : digit ? 'border-[#2A59C6]' : 'border-[#E5E7EB]'
              } focus:border-[#2A59C6]`}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {codeError && (
          <p className="text-xs text-red-500 text-center mb-4">{codeError}</p>
        )}

        <div className="text-center mb-6">
          {resendTimer > 0 ? (
            <p className="text-xs text-[#9CA3AF]">Reenviar código en {resendTimer}s</p>
          ) : (
            <button onClick={handleResendCode} disabled={sendingCode} className="text-xs text-[#2A59C6] font-semibold hover:underline">
              {sendingCode ? 'Enviando...' : 'Reenviar código'}
            </button>
          )}
        </div>

        <button
          onClick={handleVerifyAndExecute}
          disabled={code.join('').length !== 6}
          className={`w-full py-3.5 rounded-xl text-sm font-semibold transition ${
            code.join('').length === 6 ? 'bg-[#2A59C6] text-white hover:bg-[#1E4BB8]' : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
          }`}
        >
          Verificar y ejecutar inversión
        </button>
      </div>
    );
  }

  // STEP: Executing orders
  if (step === 'executing') {
    return (
      <div className="max-w-lg mx-auto text-center py-10">
        <div className="w-16 h-16 border-4 border-[#2A59C6] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <h3 className="text-lg font-bold text-[#181D27] mb-2">Ejecutando tu inversión...</h3>
        <p className="text-sm text-[#6B7280]">Comprando {breakdown.length} activos en tu portafolio {portfolio.name}</p>
      </div>
    );
  }

  // STEP: Done
  if (step === 'done') {
    const hasErrors = orderErrors.length > 0;
    const allFailed = orderResults.length === 0 && orderErrors.length > 0;
    return (
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          {allFailed ? (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </div>
              <h3 className="text-xl font-bold text-[#181D27] mb-1">Error en la inversión</h3>
              <p className="text-sm text-[#6B7280]">No se pudieron ejecutar las órdenes</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <h3 className="text-xl font-bold text-[#181D27] mb-1">¡Inversión ejecutada!</h3>
              <p className="text-sm text-[#6B7280]">{formatUSD(enVitrina.length ? montoHoy : totalAmount)} invertidos en tu Portafolio {portfolio.name}</p>
              {enVitrina.length > 0 && (
                <p className="text-xs text-[#B54708] mt-2 max-w-xs mx-auto">
                  La parte en pesos ({formatUSD(montoEspera)}) sigue en tu saldo: se invierte
                  apenas terminemos la conexión con Progresión.
                </p>
              )}
            </>
          )}
        </div>

        {orderResults.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
            <div className="text-xs font-semibold text-green-700 mb-3">Órdenes ejecutadas</div>
            <div className="space-y-2">
              {orderResults.map((r) => (
                <div key={r.symbol} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm font-semibold text-[#181D27]">{r.symbol}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-[#6B7280]">{r.status} · </span>
                    <span className="text-sm font-semibold text-[#181D27]">{formatUSD(r.notional || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {orderErrors.length > 0 && (
          <div className="bg-red-50 rounded-2xl border border-red-200 p-4 mb-4">
            <div className="text-xs font-semibold text-red-700 mb-3">Errores</div>
            <div className="space-y-2">
              {orderErrors.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-sm font-semibold text-[#181D27]">{e.symbol}</span>
                  </div>
                  <span className="text-xs text-red-600">{e.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition"
        >
          Volver a Mi Plan
        </button>
      </div>
    );
  }

  return null;
}
